// One method, returning the metadata the audio cache needs alongside the URL.
//
// `asset` rather than `audio` because the cache has to know which physical file
// it is caching and what its digest is; a bare URL string cannot say. The URL
// itself is still synchronous — appwrite's getFileDownload returns a string
// with the project as a query parameter — so it is only the *cache* that makes
// playback asynchronous.

export interface AudioAsset {
	readonly url: string;
	readonly fileId: string;
	/** Null for the bundled adapter, which has nothing to verify against. */
	readonly sha256: string | null;
	readonly size: number | null;
	/** Carried so the bytes can be checked against the type the manifest promised.
      Null for the bundled adapter, which has no manifest and no claim to check. */
	readonly mimeType: string | null;
}

export interface AssetSource {
	asset(assetId: string): AudioAsset;
}

export class BundledAssetSource implements AssetSource {
	asset(id: string): AudioAsset {
		return { url: `/audio/${id}`, fileId: id, sha256: null, size: null, mimeType: null };
	}
}

export class ManifestAssetSource implements AssetSource {
	constructor(
		private readonly manifest: {
			assets: Readonly<
				Record<string, { fileId: string; sha256: string; size: number; mimeType: string }>
			>;
		},
		private readonly urlOf: (fileId: string) => string,
	) {}

	asset(assetId: string): AudioAsset {
		// Throws rather than returning a placeholder: the bundle load has already
		// proved every referenced name resolves, so a miss here means the manifest
		// and the bundle came from different releases.
		const entry = this.manifest.assets[assetId];
		if (!entry) throw new Error(`No audio asset: ${assetId}`);
		return {
			url: this.urlOf(entry.fileId),
			fileId: entry.fileId,
			sha256: entry.sha256,
			size: entry.size,
			mimeType: entry.mimeType,
		};
	}
}
